import React from 'react';
import { COLORS } from 'pvs-design-system';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Error boundary for discrete Virtual Library content sections.
 * Catches render errors in child components so the rest of the page
 * (header, sidebar, other panels) remains usable when one section crashes.
 */
class LibraryErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Log for debugging; replace with your telemetry/logging service as needed
    console.error('[LibraryErrorBoundary] Render error caught:', error, info.componentStack);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div
          role="alert"
          style={{
            padding: '24px 16px',
            color: COLORS.red[800],
            fontFamily: "'Open Sans', Arial, 'Helvetica Neue', sans-serif",
            fontSize: 14,
          }}
        >
          Something went wrong loading this section. Please refresh the page.
        </div>
      );
    }
    return this.props.children;
  }
}

export default LibraryErrorBoundary;
